import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, formatEUR, todayISO, addDaysISO } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Save, Upload, FileText, Sparkles, X, Eye } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const genNumber = () => {
  const now = new Date();
  const yr = now.getFullYear();
  const mo = String(now.getMonth() + 1).padStart(2, "0");
  const rnd = Math.floor(Math.random() * 900 + 100);
  return `FA-${yr}${mo}-${rnd}`;
};

export default function FactureForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploadedPdf, setUploadedPdf] = useState(null); // {file_id, filename}
  const fileInputExtract = useRef(null);
  const fileInputAttach = useRef(null);

  const [form, setForm] = useState(() => ({
    numero: genNumber(),
    client_id: "",
    date_emission: todayISO(),
    date_echeance: addDaysISO(todayISO(), 30),
    montant: "",
    description: "",
  }));

  useEffect(() => {
    const loadData = async () => {
      const cs = await api.get("/clients");
      setClients(cs.data);
      if (isEdit) {
        const { data } = await api.get(`/factures/${id}`);
        setForm({
          numero: data.numero,
          client_id: data.client_id,
          date_emission: data.date_emission,
          date_echeance: data.date_echeance,
          montant: String(data.montant),
          description: data.description || "",
        });
        if (data.pdf_file_id) {
          setUploadedPdf({
            file_id: data.pdf_file_id,
            filename: "Facture jointe",
          });
        }
      }
    };
    loadData();
  }, [id, isEdit]);

  // Find a client by fuzzy name match
  const matchClientByName = (name) => {
    if (!name) return null;
    const norm = name.toLowerCase().trim();
    return (
      clients.find((c) => c.nom.toLowerCase() === norm) ||
      clients.find((c) => c.nom.toLowerCase().includes(norm) || norm.includes(c.nom.toLowerCase()))
    );
  };

  const onExtractPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Sélectionnez un fichier PDF");
      return;
    }
    setExtracting(true);
    const t = toast.loading("Analyse du PDF en cours...");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/factures/extract-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const f = data.fields || {};

      // Refetch clients to avoid stale state (race if user uploads before initial fetch resolves)
      let currentClients = clients;
      try {
        const cs = await api.get("/clients");
        currentClients = cs.data;
        setClients(currentClients);
      } catch {}

      const matchAgainst = (name) => {
        if (!name) return null;
        const norm = name.toLowerCase().trim();
        return (
          currentClients.find((c) => c.nom.toLowerCase() === norm) ||
          currentClients.find(
            (c) =>
              c.nom.toLowerCase().includes(norm) || norm.includes(c.nom.toLowerCase())
          )
        );
      };

      // Try to match client, otherwise auto-create one from the extracted name
      let matched = matchAgainst(f.client_nom);
      let clientCreated = false;
      if (!matched && f.client_nom && f.client_nom.trim()) {
        try {
          const { data: newClient } = await api.post("/clients", { nom: f.client_nom.trim() });
          setClients((prev) => [...prev, newClient]);
          matched = newClient;
          clientCreated = true;
        } catch {}
      }

      // Sensible fallbacks so auto-create can succeed
      const emission = f.date_emission || todayISO();
      const echeance = f.date_echeance || addDaysISO(emission, 30);
      const numero = f.numero || form.numero;
      const montant = f.montant != null ? Number(f.montant) : 0;

      const nextForm = {
        numero,
        client_id: matched?.id || form.client_id,
        date_emission: emission,
        date_echeance: echeance,
        montant: montant ? String(montant) : form.montant,
        description: f.description || form.description,
      };
      setForm(nextForm);
      setUploadedPdf({ file_id: data.file_id, filename: data.original_filename });

      // Auto-create the facture and add it to the tracking list
      const canAutoCreate = matched && numero && montant > 0;
      if (canAutoCreate) {
        try {
          await api.post("/factures", {
            numero,
            client_id: matched.id,
            date_emission: emission,
            date_echeance: echeance,
            montant,
            description: f.description || null,
            pdf_file_id: data.file_id,
          });
          const clientMsg = clientCreated
            ? ` (client "${matched.nom}" créé)`
            : ` (client: ${matched.nom})`;
          toast.success(`Facture ${numero} ajoutée à votre suivi${clientMsg}`, {
            id: t,
            duration: 4500,
          });
          navigate("/factures");
          return;
        } catch (err) {
          toast.warning(
            "Facture extraite mais création auto échouée. Vérifiez les champs et cliquez sur Créer.",
            { id: t }
          );
          return;
        }
      }

      // Fallback: not enough info — show what's missing
      const missing = [];
      if (!matched) missing.push("client");
      if (!numero) missing.push("numéro");
      if (montant <= 0) missing.push("montant");
      if (missing.length) {
        toast.warning(
          `Extraction OK. Complétez : ${missing.join(", ")} puis cliquez sur Créer.`,
          { id: t }
        );
      } else {
        toast.success("Facture extraite. Vérifiez et cliquez sur Créer.", { id: t });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'extraction du PDF", { id: t });
    } finally {
      setExtracting(false);
    }
  };

  const onAttachPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Sélectionnez un fichier PDF");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "facture_pdf");
    try {
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedPdf({ file_id: data.id, filename: data.original_filename });
      toast.success("PDF joint");
    } catch (err) {
      toast.error("Erreur d'upload");
    }
  };

  const removePdf = async () => {
    if (!uploadedPdf) return;
    try {
      await api.delete(`/files/${uploadedPdf.file_id}`);
      setUploadedPdf(null);
      toast.success("PDF détaché");
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Sélectionnez un client");
      return;
    }
    if (!form.montant || Number(form.montant) <= 0) {
      toast.error("Montant invalide");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        montant: Number(form.montant),
        pdf_file_id: uploadedPdf?.file_id || null,
      };
      if (isEdit) {
        await api.put(`/factures/${id}`, payload);
        toast.success("Facture mise à jour");
      } else {
        await api.post("/factures", payload);
        toast.success("Facture créée");
      }
      navigate("/factures");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="facture-form-page" className="max-w-2xl space-y-8">
      <button
        data-testid="back-btn"
        onClick={() => navigate(-1)}
        className="text-sm text-[#5C665E] hover:text-[#1A1D1A] flex items-center gap-1.5 btn-lift"
      >
        <ArrowLeft size={14} /> Retour
      </button>

      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
          {isEdit ? "Modifier la facture" : "Nouvelle facture"}
        </h1>
        <p className="text-[#5C665E] mt-2">
          Saisissez les informations, ou importez directement un PDF pour un pré-remplissage automatique.
        </p>
      </div>

      {/* Import PDF avec IA - CTA */}
      {!isEdit && (
        <div
          className="rounded-2xl p-5 border-2 border-dashed flex items-center justify-between gap-4"
          style={{ borderColor: "#B4E3C1", backgroundColor: "#F4FAF6" }}
          data-testid="ai-import-card"
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "#DFF2E4", color: "#1E6534" }}
            >
              <Sparkles size={18} />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-semibold">Importer depuis un PDF</div>
              <div className="text-xs text-[#5C665E] mt-0.5">
                L&apos;IA extrait les infos, crée le client si besoin, et ajoute la facture à votre suivi.
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="extract-pdf-button"
            onClick={() => fileInputExtract.current?.click()}
            disabled={extracting}
            className="rounded-full px-4 py-2 text-sm font-semibold text-white btn-lift focus-brand flex items-center gap-2 disabled:opacity-60 flex-shrink-0"
            style={{ backgroundColor: "#2C5545" }}
          >
            <Upload size={14} />
            {extracting ? "Analyse..." : "Choisir un PDF"}
          </button>
          <input
            ref={fileInputExtract}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={onExtractPdf}
            data-testid="extract-pdf-input"
          />
        </div>
      )}

      <form
        onSubmit={submit}
        className="rounded-2xl border bg-white p-8 space-y-6"
        style={{ borderColor: "#E5E2DC" }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="numero" className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Numéro de facture</Label>
            <Input
              id="numero"
              data-testid="form-numero"
              value={form.numero}
              onChange={(e) => setForm({ ...form, numero: e.target.value })}
              required
              className="bg-white border-[#E5E2DC] font-mono-num"
            />
            <p className="text-[10px] text-[#5C665E] mt-1">Auto-généré, modifiable.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Client</Label>
            <Select
              value={form.client_id}
              onValueChange={(v) => setForm({ ...form, client_id: v })}
            >
              <SelectTrigger data-testid="form-client-select" className="bg-white border-[#E5E2DC]">
                <SelectValue placeholder="Sélectionner un client" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {clients.length === 0 ? (
                  <div className="p-3 text-xs text-[#5C665E]">
                    Aucun client — créez-en un depuis l&apos;onglet Clients.
                  </div>
                ) : (
                  clients.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`client-option-${c.id}`}>
                      {c.nom}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="emission" className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Date d&apos;émission</Label>
            <Input
              id="emission"
              data-testid="form-date-emission"
              type="date"
              value={form.date_emission}
              onChange={(e) => setForm({ ...form, date_emission: e.target.value })}
              required
              className="bg-white border-[#E5E2DC] font-mono-num"
            />
          </div>
          <div>
            <Label htmlFor="echeance" className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Date d&apos;échéance</Label>
            <Input
              id="echeance"
              data-testid="form-date-echeance"
              type="date"
              value={form.date_echeance}
              onChange={(e) => setForm({ ...form, date_echeance: e.target.value })}
              required
              className="bg-white border-[#E5E2DC] font-mono-num"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="montant" className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Montant (€)</Label>
          <Input
            id="montant"
            data-testid="form-montant"
            type="number"
            step="0.01"
            min="0"
            value={form.montant}
            onChange={(e) => setForm({ ...form, montant: e.target.value })}
            required
            className="bg-white border-[#E5E2DC] font-mono-num text-lg"
            placeholder="0,00"
          />
          {form.montant && (
            <p className="text-xs text-[#5C665E] mt-1.5 font-mono-num">= {formatEUR(form.montant)}</p>
          )}
        </div>

        <div>
          <Label htmlFor="desc" className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Description (optionnel)</Label>
          <Textarea
            id="desc"
            data-testid="form-description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="bg-white border-[#E5E2DC]"
            placeholder="Prestation, objet de la facture..."
            rows={3}
          />
        </div>

        {/* PDF attaché */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">PDF de la facture</Label>
          {uploadedPdf ? (
            <div
              className="flex items-center justify-between gap-3 rounded-lg border p-3"
              style={{ borderColor: "#E5E2DC", backgroundColor: "#F9F8F6" }}
              data-testid="attached-pdf"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "#DFF2E4", color: "#1E6534" }}
                >
                  <FileText size={16} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{uploadedPdf.filename}</div>
                  <div className="text-xs text-[#5C665E]">PDF joint</div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <a
                  href={`${API}/files/${uploadedPdf.file_id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="preview-attached-pdf"
                  className="p-2 rounded-lg text-[#5C665E] hover:bg-white btn-lift"
                  title="Aperçu"
                >
                  <Eye size={14} />
                </a>
                <button
                  type="button"
                  data-testid="remove-attached-pdf"
                  onClick={removePdf}
                  className="p-2 rounded-lg text-[#B3261E] hover:bg-[#FCE8E8] btn-lift"
                  title="Retirer"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ) : (
            <div>
              <button
                type="button"
                data-testid="attach-pdf-button"
                onClick={() => fileInputAttach.current?.click()}
                className="rounded-lg border border-dashed w-full p-4 text-sm text-[#5C665E] hover:bg-[#F9F8F6] btn-lift flex items-center justify-center gap-2"
                style={{ borderColor: "#E5E2DC" }}
              >
                <Upload size={14} />
                Joindre un PDF (optionnel)
              </button>
              <input
                ref={fileInputAttach}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={onAttachPdf}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="cancel-form"
            className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#5C665E] hover:bg-[#F9F8F6] btn-lift"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="submit-form"
            className="rounded-full px-6 py-2.5 text-sm font-semibold text-white btn-lift focus-brand flex items-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: "#2C5545" }}
          >
            <Save size={14} />
            {saving ? "Enregistrement..." : isEdit ? "Enregistrer" : "Créer la facture"}
          </button>
        </div>
      </form>
    </div>
  );
}
